import React, { useState, useEffect } from 'react'

interface PivotCell {
  calls: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  estimated_cost_usd: number
}

interface PivotData {
  granularity: string
  dates: string[]
  activities: string[]
  matrix: Record<string, Record<string, PivotCell>>
  activity_totals: Record<string, PivotCell>
  date_totals: Record<string, PivotCell>
}

interface ActivityEntry {
  time: string
  activity: string
  description: string
}

interface ActivityDay {
  date: string
  entries: ActivityEntry[]
}

const EMPTY_CELL: PivotCell = { calls: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0, estimated_cost_usd: 0 }

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function heatColor(value: number, max: number): string {
  if (!max || !value) return 'transparent'
  const intensity = Math.min(value / max, 1)
  return `rgba(99, 179, 237, ${0.1 + intensity * 0.4})`
}

export default function UsagePage() {
  const [pivot, setPivot] = useState<PivotData | null>(null)
  const [activityLog, setActivityLog] = useState<ActivityDay[]>([])
  const [granularity, setGranularity] = useState<'daily' | 'monthly'>('daily')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadData() }, [granularity])

  async function loadData() {
    setLoading(true)
    try {
      const [pivotRes, logRes] = await Promise.all([
        fetch(`/api/system/usage/pivot?granularity=${granularity}`, { credentials: 'include' }),
        fetch('/api/system/usage/activity-log', { credentials: 'include' }),
      ])
      if (pivotRes.ok) setPivot(await pivotRes.json())
      if (logRes.ok) {
        const data = await logRes.json()
        setActivityLog(data.days || [])
      }
    } catch {}
    setLoading(false)
  }

  if (loading) return <div style={{ padding: 20 }}>Loading usage data...</div>
  if (!pivot) return <div style={{ padding: 20 }}>No usage data available.</div>

  // Find max token value for heat map
  const allValues = Object.values(pivot.date_totals).map(c => c.total_tokens)
  const maxTokens = Math.max(...allValues, 1)

  // Summary
  const totalTokens = Object.values(pivot.activity_totals).reduce((s, c) => s + c.total_tokens, 0)
  const totalCost = Object.values(pivot.activity_totals).reduce((s, c) => s + c.estimated_cost_usd, 0)
  const totalCalls = Object.values(pivot.activity_totals).reduce((s, c) => s + c.calls, 0)
  const topActivity = pivot.activities.reduce((top, a) =>
    (pivot.activity_totals[a]?.total_tokens || 0) > (pivot.activity_totals[top]?.total_tokens || 0) ? a : top
  , pivot.activities[0] || '')

  // Activity log for selected date
  const selectedLog = selectedDate ? activityLog.find(d => d.date === selectedDate) : null

  return (
    <div style={{ display: 'flex', gap: 0, height: '100%' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {/* Summary bar */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 13, opacity: 0.8 }}>
          <span><strong>{formatTokens(totalTokens)}</strong> tokens</span>
          <span><strong>${totalCost.toFixed(2)}</strong> est. cost</span>
          <span><strong>{totalCalls}</strong> API calls</span>
          <span>Top: <strong>{topActivity}</strong></span>
          <select
            value={granularity}
            onChange={e => setGranularity(e.target.value as any)}
            style={{ marginLeft: 'auto', fontSize: 12, padding: '2px 6px', background: 'var(--bg-secondary, #1e293b)', color: 'var(--text-primary, #f1f5f9)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4 }}
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        {/* Pivot table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--bg-primary, #1a1a2e)', zIndex: 2, padding: '4px 8px', textAlign: 'left', borderBottom: '2px solid rgba(255,255,255,0.15)' }}>
                  Activity
                </th>
                {pivot.dates.map(d => (
                  <th
                    key={d}
                    onClick={() => setSelectedDate(selectedDate === d ? null : d)}
                    style={{
                      padding: '4px 6px', textAlign: 'right', borderBottom: '2px solid rgba(255,255,255,0.15)',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      background: selectedDate === d ? 'rgba(99,179,237,0.2)' : undefined,
                    }}
                  >
                    {granularity === 'daily' ? d.slice(5) : d}
                  </th>
                ))}
                <th style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '2px solid rgba(255,255,255,0.15)', fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pivot.activities.map(act => (
                <tr key={act}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--bg-primary, #1a1a2e)', zIndex: 1, padding: '3px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 500 }}>
                    {act}
                  </td>
                  {pivot.dates.map(d => {
                    const cell = pivot.matrix[act]?.[d] || EMPTY_CELL
                    return (
                      <td
                        key={d}
                        title={`${cell.calls} calls, ${cell.input_tokens} in / ${cell.output_tokens} out, $${cell.estimated_cost_usd}`}
                        onClick={() => setSelectedDate(selectedDate === d ? null : d)}
                        style={{
                          padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid rgba(255,255,255,0.08)',
                          background: heatColor(cell.total_tokens, maxTokens / pivot.activities.length),
                          cursor: 'pointer',
                        }}
                      >
                        {cell.total_tokens ? formatTokens(cell.total_tokens) : ''}
                      </td>
                    )
                  })}
                  <td style={{ padding: '3px 8px', textAlign: 'right', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 600 }}>
                    {formatTokens(pivot.activity_totals[act]?.total_tokens || 0)}
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr style={{ fontWeight: 700 }}>
                <td style={{ position: 'sticky', left: 0, background: 'rgba(255,255,255,0.05)', zIndex: 1, padding: '4px 8px', borderTop: '2px solid rgba(255,255,255,0.15)' }}>
                  Total
                </td>
                {pivot.dates.map(d => {
                  const cell = pivot.date_totals[d] || EMPTY_CELL
                  return (
                    <td key={d} style={{ padding: '4px 6px', textAlign: 'right', borderTop: '2px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}>
                      {cell.total_tokens ? formatTokens(cell.total_tokens) : ''}
                    </td>
                  )
                })}
                <td style={{ padding: '4px 8px', textAlign: 'right', borderTop: '2px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}>
                  {formatTokens(totalTokens)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Inspector panel — activity log for selected date */}
      {selectedDate && (
        <div style={{ width: 320, borderLeft: '1px solid rgba(255,255,255,0.1)', padding: '12px 16px', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>{selectedDate}</h3>
            <button onClick={() => setSelectedDate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>

          {/* Token summary for this date */}
          {pivot.date_totals[selectedDate] && (
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
              {formatTokens(pivot.date_totals[selectedDate].total_tokens)} tokens •
              ${pivot.date_totals[selectedDate].estimated_cost_usd.toFixed(4)} •
              {pivot.date_totals[selectedDate].calls} calls
            </div>
          )}

          {/* Activity entries */}
          <div style={{ fontSize: 12 }}>
            {selectedLog && selectedLog.entries.length > 0 ? (
              selectedLog.entries.map((e, i) => (
                <div key={i} style={{ marginBottom: 8, padding: '6px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ opacity: 0.5, marginRight: 6 }}>{e.time}</span>
                  <span style={{ color: '#63b3ed', fontWeight: 500, marginRight: 6 }}>{e.activity}</span>
                  <span>{e.description}</span>
                </div>
              ))
            ) : (
              <div style={{ opacity: 0.5, fontStyle: 'italic' }}>No activity log entries for this date.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
